import { Alert, AlertType } from "@/atoms-react/alert/index.react.tsx";
import { Button } from "@/atoms-react/button/Button.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import { Base, CardTitle, Info, SectionTitleSmall } from "@/atoms-react/text/Text.react";
import { LanguageSelector } from "@/components/lang-select/index.react.tsx";
import { Section } from "@/components/layout/Section.react";
import { ThemeSelector } from "@/components/theme-select/index.react.tsx";
import { Routes } from "@/features/router";
import { useSecurity } from "@/features/security/hooks/use-react-security";
import { convert_naturalPerson_naturalEntityModify } from "@/features/user/converters.ts";
import { useUser } from "@/features/user/hooks/use-user.ts";
import { UserType } from "@/features/user/types.ts";
import { StorageService } from "@/features/utils/storage.ts";
import { useAppDispatch, useAppSelector } from "@/redux/hooks.ts";
import { getUser, setUser } from "@/redux/slices/userSlice.ts";
import { Themes } from "@/types/theme.ts";
import { LayoutTitle } from "@/views/_layout/index.react.tsx";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ChangePasswordModal } from "../../identity/components/security/change-password-modal.react";
import { SolidAuthnModal } from "../../identity/components/security/solid-authn-modal.react";
import { Security2faConfig } from "./components/2fa-config.react.tsx";
import { UserDevices } from "./components/user-devices.react.tsx";
import { NewWebauthnDevice } from "./components/webauthn/NewWebauthnDevice.react.tsx";

export const SecurityApp = () => {
  const { t } = useTranslation(["pass"]);
  const username = StorageService.getItem("username");
  const { getWebAuthnUserDevices, getIsWebAuthnActivated, getIs2faActive, useReset2Fa } =
    useSecurity();
  const { mutateAsync: reset2fa } = useReset2Fa();
  const { data: webAuthnUserDevices, refetch: refetchDevices } = getWebAuthnUserDevices(username);

  const { data: isWebAuthnActive, refetch: refetchWebauthState } = getIsWebAuthnActivated();
  const { data: is2faActive, refetch: refetch2faState } = getIs2faActive();

  const [passwordModal, setPasswordModal] = useState(false);
  const [resetOtpAction, setResetOtpAction] = useState(false);
  const [keyModal, setKeyModal] = useState(false);
  const [otpModal, setOtpModal] = useState(false);
  const [loaderResetOtp, setLoaderResetOtp] = useState(false);
  const [addNewWebAuthnDeviceAction, setAddNewWebAuthnDeviceAction] = useState(false);
  const [otpModalAction, setOtpModalAction] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadStore = async () => {
    await refetchDevices();
    await refetchWebauthState();
    await refetch2faState();
  };
  const { useEditProfile } = useUser();
  const { mutateAsync: editProfile } = useEditProfile();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);

  const handleLanguageChange = async (lang: string) => {
    if (user) {
      const newUser: UserType = {
        ...user,
        naturalPerson: {
          ...user.naturalPerson,
          CodeLangue: lang.toUpperCase()
        }
      };

      dispatch(setUser(newUser));
      const naturalEntity = convert_naturalPerson_naturalEntityModify(newUser.naturalPerson);
      await editProfile(naturalEntity);
    }
  };
  return (
    <>
      <LayoutTitle>
        <div className="flex items-center gap-x-2">
          {t("olkypass.mainPage.navigation_menu.link.security")}
        </div>
      </LayoutTitle>
      <Base className="mb-6">{t("olkypass.securityPage.subtitle")}</Base>
      <div className="flex w-full grow flex-col gap-y-4">
        {resetOtpAction && (
          <SolidAuthnModal
            action="2fa_reset"
            onClose={() => setResetOtpAction(false)}
            onValidated={async () => {
              try {
                setLoading(true);

                setResetOtpAction(false);
                setLoaderResetOtp(true);
                await reset2fa();
                toast.success(
                  t(
                    "olkypass.securityPage.passwordSection.2fa.modal.notifications.otp.reset_success"
                  )
                );
                setLoaderResetOtp(false);
                await loadStore();
                setLoading(false);
              } catch (_) {
                toast.error("We were unable to reset 2FA. Please try again later.");
                setLoading(false);
              }
            }}
          />
        )}

        {passwordModal && (
          <ChangePasswordModal
            onClose={() => setPasswordModal(false)}
            onValidated={() => {
              setPasswordModal(false);
              toast.success(t("olkypass.forgetPasswordPage.notifications.success.reset"));
            }}
          />
        )}

        {addNewWebAuthnDeviceAction && (
          <SolidAuthnModal
            action="new_webauthn_device"
            onClose={() => setAddNewWebAuthnDeviceAction(false)}
            onValidated={async () => {
              setTimeout(() => {
                setKeyModal(true);
              }, 50);
              setAddNewWebAuthnDeviceAction(false);
            }}
          />
        )}
        <Modal
          className="w-full min-w-lg max-w-lg items-center justify-center sm:w-full"
          open={keyModal}
          onClose={() => {
            setKeyModal(false);
          }}
        >
          <ModalContent
            title={t("olkypass.securityPage.webauthnSection.devices.table.actions.add")}
          >
            <NewWebauthnDevice
              onComplete={async () => {
                setKeyModal(false);
                await loadStore();
                toast.success(
                  t("olkypass.securityPage.webauthnSection.devices.notifications.add.success")
                );
              }}
              checkAuthorisation={false}
            />
          </ModalContent>
        </Modal>

        {otpModalAction && (
          <SolidAuthnModal
            action="2fa_activation"
            onClose={() => setOtpModalAction(false)}
            onValidated={async () => {
              setTimeout(() => {
                setOtpModal(true);
              }, 50);
              setOtpModalAction(false);
            }}
          />
        )}
        <Modal
          className="w-full min-w-lg max-w-lg items-center justify-center sm:w-full"
          open={otpModal}
          onClose={() => {
            setOtpModal(false);
          }}
        >
          <ModalContent
            title={t("olkypass.securityPage.passwordSection.2fa.modal.header_card.authentication")}
          >
            <Security2faConfig
              onComplete={async () => {
                setOtpModal(false);
                await loadStore();
              }}
            />
          </ModalContent>
        </Modal>

        {loading && <Loader center />}
        {!loading && (
          <>
            <Section className="mb-4 border-2 border-slate-50 dark:border-slate-700">
              <CardTitle className="font-semibold">
                {t("olkypass.securityPage.webauthnSection.header_card.recommended")}
              </CardTitle>
              <Base className="mt-3 block">
                {t("olkypass.securityPage.webauthnSection.body_card.message_details")}
              </Base>

              {isWebAuthnActive && (
                <div>
                  <Alert type={AlertType.Success} className="mt-4">
                    {t("olkypass.securityPage.webauthnSection.body_card.is_activated")}
                  </Alert>
                </div>
              )}
              <div className="flex flex-col">
                {!isWebAuthnActive && (
                  <Button
                    className="mt-4 max-w-80"
                    theme={Themes.primary}
                    onClick={() => setKeyModal(true)}
                  >
                    {t("olkypass.securityPage.webauthnSection.button.use_key")}
                  </Button>
                )}
              </div>
              {webAuthnUserDevices?.length && webAuthnUserDevices?.length > 0 ? (
                <UserDevices
                  webAuthnUserDevices={webAuthnUserDevices}
                  isWebAuthnActive={isWebAuthnActive}
                  refreshStore={async () => {
                    await loadStore();
                  }}
                />
              ) : (
                <></>
              )}
            </Section>

            <Section className="mb-4 border-2 border-slate-50 dark:border-slate-700">
              <CardTitle>{t("olkypass.securityPage.passwordSection.title")}</CardTitle>
              <Base className="mt-3 block">
                {t("olkypass.securityPage.passwordSection.subtitle")}
              </Base>

              <SectionTitleSmall className="mt-6">
                {t("olkypass.securityPage.passwordSection.password.title")}
              </SectionTitleSmall>
              <Info className="mt-2 block">
                {t("olkypass.securityPage.passwordSection.password.description")}
              </Info>
              <Button
                theme={Themes.white}
                className="mt-4 max-w-80"
                onClick={() => {
                  setPasswordModal(true);
                }}
              >
                {t("olkypass.securityPage.passwordSection.password.button")}
              </Button>
              <div>
                <SectionTitleSmall className="mt-6">
                  {t("olkypass.securityPage.passwordSection.2fa.title")}
                </SectionTitleSmall>
                {is2faActive && !loaderResetOtp && (
                  <Alert type={AlertType.Success} className="mt-4">
                    <div className="flex h-7 items-center">
                      <span className="h-min">
                        {t("olkypass.securityPage.passwordSection.2fa.notice_success")}
                      </span>
                    </div>
                    <Button
                      dataCy="button-reset-otp"
                      theme={Themes.white}
                      className="mt-2 max-w-80"
                      onClick={() => setResetOtpAction(true)}
                    >
                      {t("olkypass.securityPage.passwordSection.2fa.reset")}
                    </Button>
                  </Alert>
                )}
                {loaderResetOtp && <Loader />}
                {!is2faActive && !loaderResetOtp && (
                  <Alert type={AlertType.Danger} className="mt-4">
                    <div className="flex items-center dark:text-white">
                      <span>{t("olkypass.securityPage.passwordSection.2fa.notice_error")}</span>
                    </div>
                    {isWebAuthnActive && (
                      <Button
                        theme={Themes.white}
                        className="mt-4 max-w-80"
                        onClick={() => setOtpModalAction(true)}
                        dataCy={"button-open-modal-2fa"}
                      >
                        {t("olkypass.securityPage.passwordSection.2fa.button")}
                      </Button>
                    )}
                    {!isWebAuthnActive && (
                      <Button
                        theme={Themes.white}
                        className="mt-4 w-52 max-w-80 sm:w-auto"
                        onClick={() => setOtpModal(true)}
                        dataCy={"button-open-modal-2fa"}
                      >
                        {t("olkypass.securityPage.passwordSection.2fa.button")}
                      </Button>
                    )}
                  </Alert>
                )}
              </div>
            </Section>

            <Section className="mb-4 border-2 border-slate-50 dark:border-slate-700">
              <CardTitle>{t("olkypass.securityPage.settingsSection.title")}</CardTitle>
              <div className="my-4 flex gap-x-4">
                <div className="w-auto flex-initial md:w-60">
                  <Base className={"ellipsis mt-4 overflow-hidden whitespace-nowrap pb-2"}>
                    {t("olkypass.securityPage.settingsSection.language_label")}
                  </Base>
                  <LanguageSelector onChange={handleLanguageChange} />
                </div>
                <div className="w-auto flex-initial">
                  <Base className={"ellipsis mt-4 overflow-hidden whitespace-nowrap pb-2"}>
                    {t("olkypass.securityPage.settingsSection.themes.label")}
                  </Base>
                  <ThemeSelector />
                </div>
              </div>
            </Section>
            <Alert title="">
              <Info className={"font-semibold"}>
                {t("olkypass.securityPage.notice")} &nbsp;
                <Link to={Routes.support} className="fon-bold text-primary">
                  {t("olkypass.securityPage.notice_link")}
                </Link>
              </Info>
            </Alert>
          </>
        )}
      </div>
    </>
  );
};
